import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges
} from '@angular/core';
import {
  Paginate, PaginationConfig, PaginationIconSet, paginationMaximumItemsPerPage, paginationMinimumItemsPerPage
} from './pagination.types';
import { NpaOption } from '@circe/core';
import { Icons } from '@circe/icons';
import { BehaviorSubject, isObservable, of, Subscription } from 'rxjs';

@Component({
  selector: 'npa-pagination',
  templateUrl: 'pagination.component.html',
  styleUrls: ['pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input()
  get page(): Paginate | BehaviorSubject<Paginate> {
    return this.page$.value;
  }

  set page(page: Paginate | BehaviorSubject<Paginate>) {
    this._pageSubscription?.unsubscribe();
    this._pageSubscription = isObservable(page) ? page.subscribe(this.page$) : of(page).subscribe(this.page$);
  }

  @Input() config: PaginationConfig;
  @Input() totalItems: number;
  @Input() itemsPerPageLabel: string;
  @Input() disabled: boolean;
  @Input() hotRender: boolean;

  @Output() pageChange: EventEmitter<Paginate> = new EventEmitter<Paginate>();

  public itemsPerPage: number;
  public itemsPerPageOptions: NpaOption[];
  public small: boolean;
  public showItemsPerPage: boolean;
  public showPageNavigatorLabel: boolean;
  public iconHandler: Icons;
  public withIcons: boolean = true;
  public page$: BehaviorSubject<Paginate> = new BehaviorSubject<Paginate>(null);

  private _configIsSet: boolean;
  private _pageSubscription: Subscription;

  constructor(private _cd: ChangeDetectorRef) {
    this.config = {};
    this.showItemsPerPage = true;
    this.showPageNavigatorLabel = true;
    this.itemsPerPage = paginationMinimumItemsPerPage;
    this.totalItems = paginationMinimumItemsPerPage;
  }

  private _processConfiguration(config: PaginationConfig = this.config): void {
    this._configIsSet = (!!config);

    if (this._configIsSet) {
      let _showItemsPerPage: boolean = true;
      if ('showItemsPerPage' in config) {
        _showItemsPerPage = config.showItemsPerPage;
      }
      this.showItemsPerPage = _showItemsPerPage;

      let _showPageNavigatorLabel: boolean = true;
      if ('showPageNavigatorLabel' in config) {
        _showPageNavigatorLabel = config.showPageNavigatorLabel;
      }
      this.showPageNavigatorLabel = _showPageNavigatorLabel;

      let _small: boolean = false;
      if ('small' in config) {
        _small = config.small;
      }
      this.small = _small;

      let _icons: boolean | PaginationIconSet = true;
      if ('icons' in config) {
        _icons = config.icons;
        this.withIcons = !!config.icons;
      }
      this.iconHandler = new Icons(_icons);

      this._buildItemsPerPageOptions();
    }
  }

  private _buildItemsPerPageOptions(): void {
    this.itemsPerPageOptions = [
      { label: paginationMinimumItemsPerPage.toString(), value: paginationMinimumItemsPerPage },
      { label: 'All', value: this.totalItems }
    ];

    if (this.totalItems > 100) {
      this.itemsPerPageOptions = [
        { label: paginationMinimumItemsPerPage.toString(), value: paginationMinimumItemsPerPage },
        { label: '100', value: 100 },
        { label: '200', value: 200 }
      ];
    }

    if (this.totalItems > 200) {
      this.itemsPerPageOptions.push({ label: '500', value: 500 });
    }

    if (this.totalItems <= paginationMaximumItemsPerPage) {
      this.itemsPerPageOptions[this.itemsPerPageOptions.length - 1] = { label: 'All', value: this.totalItems };
    } else {
      this.itemsPerPageOptions[this.itemsPerPageOptions.length - 1] = {
        label: paginationMaximumItemsPerPage.toString(),
        value: paginationMaximumItemsPerPage
      };
    }
  }

  ngOnInit(): void {
    this._processConfiguration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hotRender) {
      if (changes.config?.currentValue) {
        this._processConfiguration();
      }
    }
    if (changes.totalItems?.currentValue) {
      this.itemsPerPage = paginationMinimumItemsPerPage;
      this._buildItemsPerPageOptions();
    }
  }

  public changeToPreviousPage(): void {
    if (!this.disabled && this.page$.value.pageFirstItem > 0) {
      let _pageFirstItem: number = this.page$.value.pageFirstItem - this.page$.value.itemsPerPage;

      if (_pageFirstItem < 0) {
        _pageFirstItem = 0;
      }

      this.pageChange.emit({
        ...this.page$.value,
        pageFirstItem: _pageFirstItem
      });
    }
  }

  public changeToNextPage(): void {
    const _lastItem: number = this.page$.value.pageFirstItem + this.itemsPerPage - 1;
    if (!this.disabled && _lastItem < this.totalItems) {
      this.pageChange.emit({
        ...this.page$.value,
        pageFirstItem: this.page$.value.pageFirstItem + this.page$.value.itemsPerPage
      });
    }
  }

  public changeItemsPerPage(e: Event): void {
   if (!this.disabled) {
     const _value: number = Number((e.target as HTMLInputElement).value);
     this.itemsPerPage = _value;
     this.pageChange.emit({
       pageFirstItem: 0,
       itemsPerPage: _value
     });
   }
  }
}
